const { logWarn, logError, logDebug } = require('./lib/logger');
const { loadScript, loadScriptWithin } = require('./lib/scriptLoader');
const { configurePrebid } = require('./lib/prebidConfig');
const { setupAutoRefresh } = require('./lib/autoRefresh');
const { applyContentAdSticky } = require('./lib/contentAdSticky');
const { createPageAdRuntime } = require('./lib/adRuntime');
const {
  applyStableAdSlotSize,
  applySlotSizeFromPrebid,
  ensureAdSlotMinHeight,
  isMobileLbSlotId,
  isMobileLbAllowedSize,
  clampMobileLbContainer
} = require('./lib/adSizeUtils');
const { onElementInViewport } = require('./lib/viewportObserver');
const { resolveCoordinatedRequestThreshold } = require('./lib/adViewability');
const { ensurePrebidFallback, PREBID_LOAD_FAILSAFE_MS } = require('./lib/prebidFallback');
const { createMemberSectionAd } = require('./lib/memberAds');
const { injectModulesBeforeSectionTitles } = require('./lib/sectionInjection');
const {
  buildHomepageGamTargeting,
  applyGamTargeting
} = require('./lib/gamTargeting');
const {
  applyPrebidTargetingToSlots,
  applySlotTargeting,
  configurePageSettings,
  enableLazyLoad
} = require('./lib/gptConfig');
const {
  setupHomepageInfeedAdsObserver,
  createHomepageInfeedAdInserter,
  syncPlacedAdCountFromDom,
  isHomepageInfeedSlotId,
  collapseHomepageInfeedAdSlot,
  restoreHomepageInfeedAdSlot
} = require('./lib/homepageInfeedAds');

const {
  resolveLanguage,
  getSlotPath,
  emitAdEvent
} = require('./adsBrandUtils');
const {
  generateHomepageMobileAdUnits,
  makeHomepageMobileContentAdUnit,
  makeHomepageMobileLbAdUnit,
  ensurePrebidAdUnitForCode,
  HOMEPAGE_AD_SIZES
} = require('./adUnitFactory');

function createHomepageMobileAds(config, options = {}) {
  const targeting = options.targeting || {};

  return function initHomepageMobileAds(ctx) {
    const {
      prebidUrl,
      gptUrl,
      _noads,
      _urlonly,
      _sponsored,
      _fr_lang,
      fwjson,
      userIds,
      pbjs,
      googletag,
      pro,
      emitter,
      isLoggedIn,
      pageType,
      auctionStrategy,
      adDiagnosticsEnabled,
      device,
      pageKey
    } = ctx;

    const brand = config.brand;
    const language = resolveLanguage(config, _fr_lang);
    const PREBID_TIMEOUT = 1500;
    const FAILSAFE_TIMEOUT = 3000;
    const SECONDS_TO_WAIT_AFTER_VIEWABILITY = 30;
    const pageTargeting = buildHomepageGamTargeting({
      fwjson,
      targeting,
      language
    });

  const mbContentSizes = HOMEPAGE_AD_SIZES.mobile.content;
  const mbLeadSizes = [[320, 50], [300, 50]];

  const CONTENT_SLOT_MIN_HEIGHT = 280;
  const LB_SLOT_MIN_HEIGHT = 50;

    const slotPrefix = config.slots.mobile.content;
  const lbSlotPrefix = config.slots.mobile.leaderboard;
  const gamPath = getSlotPath(config, 'mobile', 'content');
  const lbGamPath = getSlotPath(config, 'mobile', 'leaderboard');

  function primeContentSlotMinHeights() {
    if (typeof document === 'undefined') return;
    const slots = document.querySelectorAll('.content-ad');
    slots.forEach((slot) => ensureAdSlotMinHeight(slot, CONTENT_SLOT_MIN_HEIGHT));
  }

    // Keep late-loading promotional modules out of the mobile homepage flow.
    // Newsletter boxes and brand snippets caused large layout shifts here;
    // signed-in free members get standard ad slots instead of support prompts.
    function scheduleSectionTitleAds() {
      if (typeof document === 'undefined') return;
      if (_noads === 'yes' || _urlonly === 'yes' || _sponsored === 'yes' || pro === 'active' || !isLoggedIn) return;

      const resolvedPageType = pageType === 'section' ? 'section' : 'homepage';

    const inject = () => {
      try {
        injectModulesBeforeSectionTitles({
          moduleKey: 'member-ad',
          locationPrefix: `${resolvedPageType}_section_title`,
          maxInsertions: 2,
          // The Feed is the third Narcity heading and fourth MTL Blog heading.
          // Section injection deduplicates MTL Blog's existing Feed placement.
          preferredIndices: resolvedPageType === 'homepage' ? [brand === 'mtlblog' ? 3 : 2, 3] : [1, 3],
          observe: true,
          createModule: () => createMemberSectionAd(ctx, { slotPrefix, minHeight: CONTENT_SLOT_MIN_HEIGHT })
        });
      } catch (err) {
        logWarn('Failed to inject member ads above section titles (mobile)', err);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', inject);
    } else {
      inject();
    }

    setTimeout(inject, 1500);
    setTimeout(inject, 4500);
  }

    scheduleSectionTitleAds();

    if (_noads === 'yes' || _urlonly === 'yes' || pro === 'active') {
      emitAdEvent(emitter, brand, 'ready');
      return;
    }

  primeContentSlotMinHeights();

  function initializeHomepageMobileAds() {
    const adRuntime = createPageAdRuntime({
      brand,
      pageType: pageType || 'homepage',
      device: device || 'mobile',
      pageKey,
      auctionStrategy,
      diagnosticsEnabled: adDiagnosticsEnabled,
      pbjs,
      googletag,
      timeoutMs: PREBID_TIMEOUT
    });
    const coordinator = adRuntime.coordinator;
    const contentAdRequestThreshold = resolveCoordinatedRequestThreshold({
      coordinated: adRuntime.coordinated,
      legacyThreshold: 2000,
      device: device || 'mobile'
    });
    // Generate ad units using factory - generates 1 LB + 12 content
    const adUnits = adRuntime.coordinated
      ? []
      : generateHomepageMobileAdUnits(config, { contentCount: 15 });

    // Add second leaderboard
    if (!adRuntime.coordinated) adUnits.push({
      code: `${lbSlotPrefix}-2`,
      ortb2Imp: {
        ext: {
          gpid: `${lbGamPath}#2`,
          pbadslot: `${lbGamPath}#2`
        }
      },
      mediaTypes: {
        banner: {
          sizes: mbLeadSizes
        }
      },
      bids: adUnits.find((u) => u.code === `${lbSlotPrefix}-1`)?.bids || []
    });

    pbjs.que.push(function () {
      configurePrebid(pbjs, userIds);
      if (adRuntime.coordinated) {
        return;
      }
      pbjs.addAdUnits(adUnits);
      pbjs.requestBids({
        timeout: PREBID_TIMEOUT,
        bidsBackHandler: function () {
          initAdserver();
        }
      });
    });

    function initAdserver() {
      if (!pbjs || typeof pbjs.getBidResponses !== 'function' || pbjs.initAdserverSet) {
        return;
      }
      pbjs.initAdserverSet = true;
    }

    if (!adRuntime.coordinated) {
      setTimeout(initAdserver, FAILSAFE_TIMEOUT);
    }

    googletag.cmd.push(function () {
      const REFRESH_KEY = 'refresh';
      const REFRESH_VALUE = 'true';
      adRuntime.observeGpt(googletag.pubads());

      if (!adRuntime.coordinated) {
        enableLazyLoad(googletag, googletag.pubads(), {
          fetchMarginPercent: 100,
          renderMarginPercent: 25,
          mobileScaling: 1.5
        });
      }

      googletag
        .pubads()
        .addEventListener('slotRenderEnded', function (event) {
          const slot = event.slot;
          const slotId = slot.getSlotElementId();
          const div = document.getElementById(slotId);
          const size = event.size;
          const isInfeedSlot = isHomepageInfeedSlotId(slotId, slotPrefix);

          if (isInfeedSlot && event.isEmpty && div) {
            if (collapseHomepageInfeedAdSlot(div)) {
              return;
            }
          }

          if (isInfeedSlot && !event.isEmpty && div) {
            restoreHomepageInfeedAdSlot(div);
          }

          if (size && size.length === 2 && div) {
            if (isMobileLbSlotId(slotId) && !isMobileLbAllowedSize(size[0], size[1])) {
              logWarn('mobile-lb oversized creative rejected', { slotId, size });
              clampMobileLbContainer(div);
              const iframe = div.querySelector('iframe');
              if (iframe) iframe.style.display = 'none';
            } else {
              applyStableAdSlotSize(div, size);
            }
          }
          if (isInfeedSlot && !event.isEmpty && div) {
            applyContentAdSticky(div, size);
          }
          // Reset container size if slot is empty (GPT returned no ad)
          // Keep min-height to avoid CLS when empty creatives collapse.
          if (event.isEmpty && div) {
            div.style.height = 'auto';
          }
        });

      applyGamTargeting(googletag, pageTargeting);

      // PPID = the derived, consent-gated ads id only (2026-09-09); no sharedId fallback.
      if (window.ppid) {
        googletag.pubads().setPublisherProvidedId(window.ppid);
      }

      configurePageSettings(googletag, googletag.pubads(), {
        disableInitialLoad: true,
        singleRequest: true,
        collapseDiv:
          typeof window !== 'undefined' && window._millaShowAdPlaceholders === true
            ? null
            : 'ON_NO_FILL'
      });
      googletag.enableServices();

      setupAutoRefresh(googletag, pbjs, {
        refreshKey: REFRESH_KEY,
        refreshValue: REFRESH_VALUE,
        prebidTimeout: PREBID_TIMEOUT,
        emptyInitialDelayMs: adRuntime.coordinated ? 15000 : undefined,
        emptyDelayMs: adRuntime.coordinated ? 30000 : 10000,
        filledDelayMs: SECONDS_TO_WAIT_AFTER_VIEWABILITY * 1000,
        viewabilityThreshold: 0.5,
        viewabilityTimeMs: 1000,
        maxRefreshes: 10,
        maxEmptyRefreshes: adRuntime.coordinated ? 2 : undefined,
        maxFilledRefreshes: adRuntime.coordinated ? 10 : undefined,
        preloadMarginPx: adRuntime.coordinated ? 0 : undefined,
        coordinator,
        shouldManageSlot: function (slotId) {
          return (
            slotId.indexOf(lbSlotPrefix) !== -1 ||
            slotId.indexOf(slotPrefix) !== -1
          );
        }
      });

      const mobileLbSlot1Id = `${lbSlotPrefix}-1`;
      const shouldDefineMobileLbSlot1 = !coordinator
        || !!document.getElementById(mobileLbSlot1Id);
      const mobileLbSlot1 = shouldDefineMobileLbSlot1
        ? applySlotTargeting(
          googletag.defineSlot(lbGamPath, mbLeadSizes, mobileLbSlot1Id),
          {
            lazyload: 'lazyload',
            [REFRESH_KEY]: REFRESH_VALUE
          }
        ).addService(googletag.pubads())
        : null;

      const mobileLbSlot2 = applySlotTargeting(
        googletag.defineSlot(lbGamPath, mbLeadSizes, `${lbSlotPrefix}-2`),
        {
          lazyload: 'lazyload',
          [REFRESH_KEY]: REFRESH_VALUE
        }
      ).addService(googletag.pubads());

      // Display floating banner - required for disableInitialLoad
      googletag.display(`${lbSlotPrefix}-2`);

      if (coordinator) {
        const initialLeaderboardSlotIds = [];
        if (mobileLbSlot1) {
          coordinator.registerSlot({
            slotId: mobileLbSlot1Id,
            gptSlot: mobileLbSlot1,
            createAdUnit: () => makeHomepageMobileLbAdUnit(1, config),
            placement: { slotFamily: 'leaderboard', slotIndex: 1 }
          });
          initialLeaderboardSlotIds.push(mobileLbSlot1Id);
        }
        coordinator.registerSlot({
          slotId: `${lbSlotPrefix}-2`,
          gptSlot: mobileLbSlot2,
          createAdUnit: () => makeHomepageMobileLbAdUnit(2, config),
          placement: { slotFamily: 'leaderboard', slotIndex: 2 }
        });
        initialLeaderboardSlotIds.push(`${lbSlotPrefix}-2`);
        coordinator.requestInitial(initialLeaderboardSlotIds);
      } else {
        pbjs.requestBids({
          timeout: PREBID_TIMEOUT,
          adUnitCodes: [`${lbSlotPrefix}-1`, `${lbSlotPrefix}-2`],
          bidsBackHandler: function () {
            applySlotSizeFromPrebid(`${lbSlotPrefix}-1`, pbjs);
            applySlotSizeFromPrebid(`${lbSlotPrefix}-2`, pbjs);
            applyPrebidTargetingToSlots(pbjs, [mobileLbSlot1, mobileLbSlot2]);
            googletag.pubads().refresh([mobileLbSlot1, mobileLbSlot2]);
          }
        });
      }

      onElementInViewport({
        selector: '.content-ad',
        onIntersect: function (entry) {
          const slotId = entry && entry.target && entry.target.id;
          if (!slotId) return;
          logDebug('slot:define', {
            slotId,
            adUnit: gamPath
          });
          const adSlot = applySlotTargeting(
            googletag.defineSlot(
              gamPath,
              mbContentSizes,
              slotId
            ),
            {
              lazyload: 'lazyload',
              [REFRESH_KEY]: REFRESH_VALUE
            }
          ).addService(googletag.pubads());
          logDebug('slot:display', { slotId });
          googletag.display(slotId);

          if (coordinator) {
            const slotIndex = Number((slotId.match(/-(\d+)$/) || [])[1]);
            coordinator.registerSlot({
              slotId,
              gptSlot: adSlot,
              createAdUnit: () => makeHomepageMobileContentAdUnit(slotIndex, config),
              placement: { slotFamily: 'content', slotIndex }
            });
            coordinator.requestInitial(slotId);
          } else {
            pbjs.que.push(function () {
              try {
                ensurePrebidAdUnitForCode(pbjs, slotId, config, 'mobile');
              } catch (err) {
                logWarn('Failed to ensure Prebid ad unit for slot', err);
              }

              logDebug('slot:requestBids', { slotId, timeout: PREBID_TIMEOUT });
              pbjs.requestBids({
                timeout: PREBID_TIMEOUT,
                adUnitCodes: [slotId],
                bidsBackHandler: function () {
                  googletag.cmd.push(function () {
                    logDebug('slot:bidsBack', { slotId });
                    applySlotSizeFromPrebid(slotId, pbjs);
                    applyPrebidTargetingToSlots(pbjs, adSlot);
                    logDebug('slot:refresh', { slotId, reason: 'initial' });
                    googletag.pubads().refresh([adSlot]);
                  });
                }
              });
            });
          }
        },
        threshold: contentAdRequestThreshold,
        oneTime: true
      });
    });
  }

  loadScript(gptUrl)
    .then(() => {
      return loadScriptWithin(prebidUrl, PREBID_LOAD_FAILSAFE_MS)
        .then(() => {
          initializeHomepageMobileAds();
        })
        .catch((err) => {
          logWarn('Prebid failed to load; continuing with GPT-only ads', err);
          ensurePrebidFallback(pbjs);
          initializeHomepageMobileAds();
        });
    })
    .catch((err) => {
      logError('Failed to load GPT script (homepage mobile)', err);
      emitAdEvent(emitter, brand, 'error', err);
    });

  if (!window.adData || typeof window.adData.placedAdCount !== 'number') {
    window.adData = { placedAdCount: 1 };
  }

  // Sync counter with any server-rendered or previously-inserted content ads
  syncPlacedAdCountFromDom(slotPrefix);

  // Inject mobile floating banner for leaderboard slot 2
  if (!document.querySelector('.floating-banner')) {
    document.body.insertAdjacentHTML(
      'beforeend',
      `<div class="floating-banner" data-value="btest-324"><div id="${lbSlotPrefix}-2" class="banner-content"></div></div>`
    );
  }
  ensureAdSlotMinHeight(
    document.getElementById(`${lbSlotPrefix}-1`),
    LB_SLOT_MIN_HEIGHT
  );
  clampMobileLbContainer(document.getElementById(`${lbSlotPrefix}-1`));
  ensureAdSlotMinHeight(
    document.getElementById(`${lbSlotPrefix}-2`),
    LB_SLOT_MIN_HEIGHT
  );
  clampMobileLbContainer(document.getElementById(`${lbSlotPrefix}-2`));

  // Setup insertNewAd function for in-feed ads
  window.insertNewAd = createHomepageInfeedAdInserter(slotPrefix, CONTENT_SLOT_MIN_HEIGHT);

  // Setup in-feed ads observer
  setupHomepageInfeedAdsObserver({
    selectors: ['.container.feed .posts-wrapper'],
    widgetClass: 'widget',
    slotPrefix,
    adInterval: 4,
    minHeight: CONTENT_SLOT_MIN_HEIGHT
  });

    emitAdEvent(emitter, brand, 'ready');
  };
}

module.exports = {
  createHomepageMobileAds
};
